import { EAccountSummaryRoundingOptions, IAccountSummaryFormatting } from '@opengov/reporting-types';

export const EN_DASH = '–';

const formatNegativeNumberStringInAccountingStyle = (value: string) => {
  return `${value.replace('-', '(')})`;
};

const isInvalidValue = (value: unknown) => {
  const isEmpty = `${value}`.trim().length === 0;
  return value === undefined || value === null || isNaN(Number(value)) || isEmpty;
};

const roundToDigits = (value: number, digit: number) => {
  const roundedValue = Math.round(value / digit) * digit;
  // To prevent negative zero
  if (1 / roundedValue === -Infinity || 1 / roundedValue === Infinity) {
    return Math.abs(roundedValue);
  }
  return roundedValue;
};

// Return info that we need for formatting
const roundingByOption = (value: number, formattingOption) => {
  let roundedValue = value;
  let maximumFractionDigits = 0;
  if (formattingOption < 0) {
    // Gotta be decimals
    maximumFractionDigits = Math.abs(formattingOption);
  } else {
    roundedValue = roundToDigits(value, 10 ** formattingOption);
  }

  return {
    maximumFractionDigits: maximumFractionDigits,
    roundedValue: roundedValue
  };
};

const formatCurrencyWithSymbol = (
  roundingInfo: { maximumFractionDigits: number; roundedValue: number },
  currentLocale: string,
  display: IAccountSummaryFormatting['negativeNumberDisplay']
): string => {
  const currencyFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: roundingInfo.maximumFractionDigits,
    ...(roundingInfo.maximumFractionDigits <= 2 && { minimumFractionDigits: roundingInfo.maximumFractionDigits }),
    ...(display === EAccountSummaryRoundingOptions.Accounting && {
      currencySign: 'accounting'
    })
  });
  return currencyFormatter.format(roundingInfo.roundedValue);
};

const formatCurrencyWithoutSymbol = (
  roundingInfo: { maximumFractionDigits: number; roundedValue: number },
  currentLocale: string,
  display: IAccountSummaryFormatting['negativeNumberDisplay']
): string => {
  const numberFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'decimal',
    maximumFractionDigits: roundingInfo.maximumFractionDigits,
    ...(roundingInfo.maximumFractionDigits <= 2 && { minimumFractionDigits: roundingInfo.maximumFractionDigits })
  });
  if (display === EAccountSummaryRoundingOptions.Accounting && roundingInfo.roundedValue < 0) {
    return formatNegativeNumberStringInAccountingStyle(numberFormatter.format(roundingInfo.roundedValue));
  }
  return numberFormatter.format(roundingInfo.roundedValue);
};

const formatCurrency = (
  val,
  currentLocale: string,
  formattingOption: IAccountSummaryFormatting['currencyRounding'] = EAccountSummaryRoundingOptions.Ones,
  display: IAccountSummaryFormatting['negativeNumberDisplay'] = EAccountSummaryRoundingOptions.Standard,
  showCurrencySymbol: boolean = true
): string => {
  if (isInvalidValue(val)) {
    return EN_DASH;
  }

  const roundingInfo = roundingByOption(val, formattingOption);

  return showCurrencySymbol
    ? formatCurrencyWithSymbol(roundingInfo, currentLocale, display)
    : formatCurrencyWithoutSymbol(roundingInfo, currentLocale, display);
};

const formatNumber = (
  val,
  currentLocale: string,
  formattingOption: IAccountSummaryFormatting['numberRounding'] = EAccountSummaryRoundingOptions.Ones,
  display: IAccountSummaryFormatting['negativeNumberDisplay'] = EAccountSummaryRoundingOptions.Standard
): string => {
  if (isInvalidValue(val)) {
    return EN_DASH;
  }
  const roundingInfo = roundingByOption(val, formattingOption);
  const numberFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'decimal',
    maximumFractionDigits: roundingInfo.maximumFractionDigits,
    // Forcing decimal digits to prevent ending 0 get thrown
    ...(roundingInfo.maximumFractionDigits <= 2 && { minimumFractionDigits: roundingInfo.maximumFractionDigits })
  });
  const displayInAccountingNegative =
    display === EAccountSummaryRoundingOptions.Accounting && roundingInfo.roundedValue < 0;
  if (displayInAccountingNegative) {
    return formatNegativeNumberStringInAccountingStyle(numberFormatter.format(roundingInfo.roundedValue));
  }

  return numberFormatter.format(roundingInfo.roundedValue);
};

const formatPercent = (
  val,
  currentLocale: string,
  formattingOption: IAccountSummaryFormatting['percentRounding'] = EAccountSummaryRoundingOptions.Ones,
  display: IAccountSummaryFormatting['negativeNumberDisplay'] = EAccountSummaryRoundingOptions.Standard
): string => {
  if (isInvalidValue(val)) {
    return EN_DASH;
  }
  // We need to times 100 for rounding, then divide 100 to apply percent style
  const roundingInfo = roundingByOption(val * 100, formattingOption);
  const percentFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'percent',
    maximumFractionDigits: roundingInfo.maximumFractionDigits,
    // Forcing decimal digits to prevent ending 0 get thrownU
    ...(roundingInfo.maximumFractionDigits <= 2 && { minimumFractionDigits: roundingInfo.maximumFractionDigits })
  });

  const displayInAccountingNegative =
    display === EAccountSummaryRoundingOptions.Accounting && roundingInfo.roundedValue < 0;
  if (displayInAccountingNegative) {
    return formatNegativeNumberStringInAccountingStyle(percentFormatter.format(roundingInfo.roundedValue / 100));
  }

  return percentFormatter.format(roundingInfo.roundedValue / 100);
};

const formatDate = (val, currentLocale: string): string => {
  // We are not using this now, but for legacy we use standard-short as the config below
  return new Intl.DateTimeFormat(currentLocale, { month: '2-digit', day: '2-digit', year: 'numeric' }).format(val);
};

export const accountSummaryFormatters = (
  formattingOptions?: IAccountSummaryFormatting,
  currentLocale: string = 'en-US'
) => {
  const display = formattingOptions?.negativeNumberDisplay;
  const showSymbol = formattingOptions?.showCurrencySymbol ?? true;
  return {
    currency: (val) => formatCurrency(val, currentLocale, formattingOptions?.currencyRounding, display, showSymbol),
    number: (val) => formatNumber(val, currentLocale, formattingOptions?.numberRounding, display),
    percent: (val) => formatPercent(val, currentLocale, formattingOptions?.percentRounding, display),
    date: (val) => formatDate(val, currentLocale)
  };
};

export const prefixCurrencySymbol = (formattedValue: string, currentLocale: string = 'en-US'): string => {
  const currencySymbol =
    new Intl.NumberFormat(currentLocale, { style: 'currency', currency: 'USD' })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? '$';

  if (formattedValue === EN_DASH || formattedValue.trim() === '') {
    return `${currencySymbol}${EN_DASH}`;
  }
  return formattedValue;
};

export const accountSummaryRounders = (
  formattingOptions?: IAccountSummaryFormatting,
  currentLocale: string = 'en-US'
) => {
  return {
    currency: (val) => roundingByOption(val, formattingOptions?.currencyRounding),
    number: (val) => roundingByOption(val, formattingOptions?.numberRounding),
    // We need to times 100 for rounding, then divide 100 to apply percent style
    percent: (val) => roundingByOption(val * 100, formattingOptions?.percentRounding),
    date: (val) => roundingByOption(val, currentLocale)
  };
};

export const roundWithAccountSummaryRounder = (
  accountSummaryTableFormatting: IAccountSummaryFormatting,
  value: number,
  format?: 'currency' | 'percentage' | 'numeric' | string
): number | string => {
  const rounder = accountSummaryRounders(accountSummaryTableFormatting);

  switch (format) {
    case 'currency':
      return rounder.currency(value).roundedValue;
    case 'percentage':
      return rounder.percent(value).roundedValue;
    case 'numeric':
      return rounder.number(value).roundedValue;
    default:
      return value;
  }
};
